/**
 * Lightweight Google Analytics 4 (gtag.js) wrapper — spec 049-google-analytics.
 *
 * Privacy-by-default (NFR-001 / GDPR):
 *  - Nothing is loaded and no event is dispatched until `initGA()` runs, which
 *    the app only calls after the user explicitly accepts cookies (see
 *    CookieConsent.vue / App.vue).
 *  - When `VITE_GA_MEASUREMENT_ID` is empty (local dev, tests, or unconfigured
 *    builds) every exported function is a no-op, so GA stays fully inactive
 *    (NFR-003 — dev/test traffic never reaches a GA property).
 *  - `gtag.js` is injected asynchronously so it never blocks the landing-page
 *    critical path (NFR-002).
 */

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

/** localStorage key holding the user's cookie-consent decision. */
export const CONSENT_KEY = 'cpt_cookie_consent';

export type ConsentValue = 'accepted' | 'declined';

let initialized = false;

function resolveMeasurementId(explicit?: string): string {
  const fromEnv = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
  return (explicit ?? fromEnv ?? '').trim();
}

/** Returns the stored consent decision, or null if the user has not chosen. */
export function getConsent(): ConsentValue | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'accepted' || v === 'declined' ? v : null;
  } catch {
    return null;
  }
}

/** Persists the user's consent decision. */
export function setConsent(value: ConsentValue): void {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    /* storage unavailable (private mode) — non-fatal, GA simply stays off */
  }
}

/** Clears the stored consent so the banner can be shown again. */
export function clearConsent(): void {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* non-fatal */
  }
}

/** True once GA has been initialized with a non-empty measurement ID. */
export function isGAActive(): boolean {
  return initialized && resolveMeasurementId() !== '';
}

/**
 * Boots gtag.js for the given measurement ID. Safe to call repeatedly — it
 * initializes at most once and no-ops when the ID is empty.
 */
export function initGA(measurementId?: string): void {
  if (initialized) return;
  const id = resolveMeasurementId(measurementId);
  if (!id) return; // empty ID → keep GA completely off (local/test)

  // Bootstrap the dataLayer + gtag shim before the remote script loads.
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', id, {
    // Router drives page_view manually (FR-002); avoid the auto double-count.
    send_page_view: false,
    anonymize_ip: true,
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  initialized = true;
}

/** Records a manual page_view (FR-002). No-op until GA is active. */
export function trackPageview(path: string, title?: string): void {
  if (!isGAActive() || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title || document.title,
    send_to: resolveMeasurementId(),
  });
}

/** Records a custom GA4 event (FR-004). No-op until GA is active. */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  if (!isGAActive() || typeof window.gtag !== 'function') return;
  window.gtag('event', name, params);
}
